import { FunctionComponent, useState } from "react";
import {
  useElements,
  useStripe,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { TextInput } from "src/components/inputs";
import { Button } from "src/components/generic";
import { dynamo, format, stripe } from "src/utils";
import { useAuth } from "src/context/AuthContext";
import { userTypes } from "src/utils/types";
import { INote } from "src/utils/types/NoteTypes";

interface ICheckoutFormProps {
  amount: number;
  type: "intake" | "followup";
  updatePaymentIntent: (newAmount: number) => void;
}

const AppointmentCheckoutForm: FunctionComponent<ICheckoutFormProps> = ({
  amount,
  type,
  updatePaymentIntent,
}) => {
  const intakeLink = process.env.REACT_APP_IA_LINK || "";
  const followupLink = process.env.REACT_APP_FU_LINK || "";
  const elements = useElements();
  const stripeConnection = useStripe();
  const { user } = useAuth();

  const [couponCode, setCouponCode] = useState<string>("");
  const [couponCodeInvalid, setCouponCodeInvalid] = useState<{
    status: boolean;
    message: string;
  }>({ status: true, message: "" });
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const formattedAmount: string =
    amount >= 0 ? `$${(amount / 100).toFixed(2)}` : "loading...";

  const confirmCouponCode = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const couponValidity = await stripe.payments.validateCouponCode(
        couponCode
      );

      const amountOff = couponValidity?.coupon?.amount_off;
      const formattedAmountOff = `$${(amountOff / 100).toFixed(2)}`;

      if (amountOff > 0) {
        await updatePaymentIntent(amount - amountOff);

        setCouponApplied(true);
        setCouponCodeInvalid({
          message: `You Saved ${formattedAmountOff} on your appointment!`,
          status: !couponValidity?.valid, // flip valid to set invalid status i.e. if valid then !invalid, if !valid then invalid
        });
      } else {
        setCouponCodeInvalid({
          message: ``,
          status: !couponValidity?.valid, // flip valid to set invalid status i.e. if valid then !invalid, if !valid then invalid
        });
      }
    } catch (error: any) {
      const { data } = error?.response;

      if (data?.error && data?.valid === false) {
        setCouponCodeInvalid({
          message: data.error,
          status: !data.valid, // flip valid to set invalid status i.e. if valid then !invalid, if !valid then invalid
        });

        setError("Invalid Code");
      }
    }
  };

  const submitPayment = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      if (!elements || !stripeConnection) return;

      if (user?.userType === userTypes.AccountTypes.CLIENT) {
        const note: INote = {
          id: user && user?.notes?.length ? user.notes.length : 0,
          text: `NEATT Appointment Scheduled ${new Date().toLocaleString()}`,
          date: new Date(),
          author: {
            id: "TBD",
            name: "ClientVitals",
          },
        };

        format.users.addClientNote(user, note);

        await dynamo.mutations.putUser(user);
      }

      const { error } = await stripeConnection.confirmPayment({
        elements,
        confirmParams: {
          return_url: type === "intake" ? intakeLink : followupLink,
        },
      });

      if (error) {
        setError(error.message || "Payment Confirmation Failed");
      }
    } catch (error: any) {
      setError(error.message || "Payment Failed");
    }
  };

  return (
    <form>
      {error && <p>{error}</p>}
      <div>
        {couponCodeInvalid.status && (
          <p className="text-[red]">{couponCodeInvalid.message}</p>
        )}
        {!couponCodeInvalid.status && (
          <p className="text-[green]">{couponCodeInvalid.message}</p>
        )}
        <TextInput
          id="couponCodeInput"
          value={couponCode}
          onChange={(code) => setCouponCode(code)}
          placeholder="Enter Coupon Code"
        />
        <Button
          text="Apply Coupon"
          styles="w-[200px]"
          onClick={confirmCouponCode}
          disabled={couponApplied}
        />
      </div>

      <div id="payment-form" className="w-[85%] lg:w-[60%]">
        <PaymentElement
          id="payment-element"
          options={{ layout: "accordion" }}
        />
        {/* {message && <div id="payment-message">{message}</div>} */}
      </div>

      <div>
        <p className="font-semibold">
          {
            // "appointment type: $XX.XX"
            `${
              type === "intake"
                ? "Pre-Qualification Intake & Assessment (NEATT)"
                : "Follow-Up Appointment"
            }: ${formattedAmount}`
          }
        </p>
        <p>{`Total: ${formattedAmount}`}</p>

        <Button
          text="Confirm Payment"
          styles="w-[200px]"
          onClick={submitPayment}
        />
      </div>
    </form>
  );
};

export default AppointmentCheckoutForm;
